// // src/components/myblogs/MyBlogs.js

import React, { useState, useEffect } from "react";
import axios from "../../axiosConfig"; // Import configured Axios instance
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Button,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";

// ✅ Styled Components (Replaces makeStyles)
const RootContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const StyledCard = styled(Card)(({ theme }) => ({
  border: "1px solid #cbcaca",
  height: "100%",
  display: "flex",
  borderRadius: "8px",
  flexDirection: "column",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.5s",
  "&:hover": {
    transform: "scale(1.05)",
  },
  width: "300px"
}));

const CardImage = styled("img")(({ theme }) => ({
  objectFit: "contain",
  marginBottom: theme.spacing(2),
  borderRadius: "4px 4px 0 0",
}));

const MessageContainer = styled("div")(({ theme }) => ({
  textAlign: "center",
  marginTop: theme.spacing(4),
}));

const MyBlogs = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("jwtToken")
  );

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get("/api/my-blogs");
        setBlogs(response.data.blogs || []);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          setIsAuthenticated(false);
        } else {
          console.error("Error fetching user blogs:", error);
        }
      }
    };

    if (isAuthenticated) {
      fetchBlogs();
    }
  }, [isAuthenticated]);

  const publishedBlogs = blogs.filter((blog) => blog.status === 'published');
  const draftBlogs = blogs.filter((blog) => blog.status === 'draft');

  if (!isAuthenticated) {
    return (
      <RootContainer>
        <Typography variant="h4" gutterBottom sx={{ textAlign: "center" }}>
          You must be logged in to view your blogs.
        </Typography>
        <MessageContainer>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/login")}
          >
            Log In
          </Button>
        </MessageContainer>
      </RootContainer>
    );
  }

  const renderBlogGrid = (list, emptyText) => {
    if (list.length < 1) {
      return (
        <MessageContainer variant="h6" color="textSecondary">
          {emptyText}
        </MessageContainer>
      );
    }

    return (
      <Grid container spacing={3} className="cards-container">
        {list.map((blog) => (
          <Grid item xs={12} sm={6} md={4} key={blog._id}>
            <Link
              to={`/blog/${blog._id}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <StyledCard>
                <CardImage src={blog.imageUrl} alt={blog.title} />
                <CardContent>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                    {blog.title}
                  </Typography>
                  <Typography color="textSecondary" gutterBottom>
                    {blog.status === 'draft' ? 'Draft' : 'Published'} • {blog.category || 'General'}
                  </Typography>
                  <Typography variant="body2" component="p" dangerouslySetInnerHTML={{ __html: (blog.excerpt || blog.content || '').substring(0, 100) + (blog.excerpt || blog.content ? '...' : '') }}>
                  </Typography>
                </CardContent>
              </StyledCard>
            </Link>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <RootContainer>
      <Typography variant="h4" gutterBottom>
        My Blogs
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Published
      </Typography>
      {renderBlogGrid(publishedBlogs, 'You have no published blogs yet.')}

      <Typography variant="h5" gutterBottom sx={{ mt: 5, mb: 2 }}>
        Drafts
      </Typography>
      {renderBlogGrid(draftBlogs, 'You have no saved drafts yet.')}
    </RootContainer>
  );
};

export default MyBlogs;
